// Series subscriptions — the pure core (design_documents/Comics/series-subscriptions.md).
//
// A subscription follows a Metron series (plus its associated annuals/specials)
// for new single issues, and an Open Library title watch for new collected
// editions. Each check diffs what the sources say now against what the
// subscription has already seen, and turns the difference into bell notices:
//   • announced — a new issue appeared, on sale later      (decision 5)
//   • out       — an issue's on-sale date has arrived
//   • trade     — a new collected edition appeared (Open Library; no dates)
// Several of one kind for one series on one day collapse into one notice.

import { collapseIssueNumbers, compareIssueNumbers } from "./comics.js";

// How far back the issue check looks (so an issue that went on sale while the
// app wasn't opened still gets its "out" notice).
export const LOOKBACK_DAYS = 7;
// A finished series is re-checked weekly rather than daily (decision 9); the
// trade watch is weekly for everyone (Open Library lags months anyway).
export const SLOW_CHECK_DAYS = 7;
// Seen-id lists are capped so a long-running subscription can't grow forever.
const SEEN_CAP = 400;

export function addDays(dateStr, n) {
	const d = new Date(`${dateStr}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
}

export function daysBetween(a, b) {
	return Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
}

export function isOngoing(sub) {
	return !sub?.status || sub.status === "Ongoing";
}

// Is the issue check due today? Daily for ongoing series, weekly once ended.
export function issueCheckDue(sub, today) {
	if (!sub.last_checked) return true;
	const gap = daysBetween(sub.last_checked, today);
	return isOngoing(sub) ? gap >= 1 : gap >= SLOW_CHECK_DAYS;
}

export function tradeCheckDue(sub, today) {
	if (!sub.trade_query) return false;
	return !sub.last_trade_check || daysBetween(sub.last_trade_check, today) >= SLOW_CHECK_DAYS;
}

// The series ids a check covers: the subscribed series + enabled associated ones.
export function watchedSeriesIds(sub) {
	return [sub.series_id, ...(sub.associated ?? []).filter((a) => a.enabled !== false).map((a) => a.id)];
}

const capped = (list) => list.slice(-SEEN_CAP);

// Diff fresh issue rows (Metron list rows with store_date, from every watched
// series) against the subscription. Pure: returns { next, announced, out }.
//   announced — issues not seen before whose on-sale date is still ahead
//   out       — issues whose on-sale date has arrived since they were last
//               reported out (new-and-already-out issues land here too)
// On the very first check (`silent`), everything is recorded and nothing is
// reported, so subscribing doesn't dump a backlog into the bell.
export function diffIssues(sub, rows, today, { silent = false } = {}) {
	const seen = new Set((sub.seen_issue_ids ?? []).map(Number));
	const outDone = new Set((sub.out_issue_ids ?? []).map(Number));
	const announced = [];
	const out = [];
	for (const r of rows) {
		const id = Number(r.issue_id);
		const date = r.store_date ?? null;
		const isNew = !seen.has(id);
		const released = date != null && date <= today;
		if (released && !outDone.has(id)) {
			// Only "out" if it's recent — never report an old back issue as out today.
			if (!silent && daysBetween(date, today) <= LOOKBACK_DAYS) out.push(r);
			outDone.add(id);
		} else if (isNew && !released && !silent) {
			announced.push(r);
		}
		seen.add(id);
	}
	const slim = (r) => ({
		issue_id: Number(r.issue_id),
		label: r.label,
		number: r.number ?? null,
		series_id: r.series?.id ?? null,
		store_date: r.store_date,
		cover_url: r.cover_url ?? null,
	});
	// On sale in the last two weeks — the pull list's "out this week" (roadmap #50).
	const recent_out = rows
		.filter((r) => r.store_date && r.store_date <= today && daysBetween(r.store_date, today) <= 13)
		.map(slim)
		.sort((a, b) => a.store_date.localeCompare(b.store_date));
	const upcoming = rows
		.filter((r) => r.store_date && r.store_date > today)
		.map((r) => ({
			issue_id: Number(r.issue_id),
			label: r.label,
			number: r.number ?? null,
			series_id: r.series?.id ?? null,
			store_date: r.store_date,
			cover_url: r.cover_url ?? null,
		}))
		.sort((a, b) => a.store_date.localeCompare(b.store_date));
	return {
		next: {
			...sub,
			seen_issue_ids: capped([...seen]),
			out_issue_ids: capped([...outDone]),
			upcoming,
			recent_out,
			last_checked: today,
		},
		announced,
		out,
	};
}

// Diff Open Library title-watch results ({ ol_id, title, published_year, … })
// against the trades already seen. Returns { next, trades }.
export function diffTrades(sub, books, today, { silent = false } = {}) {
	const seen = new Set(sub.seen_trade_keys ?? []);
	const trades = [];
	for (const b of books) {
		if (!b?.ol_id || seen.has(b.ol_id)) continue;
		seen.add(b.ol_id);
		if (!silent) trades.push(b);
	}
	return { next: { ...sub, seen_trade_keys: capped([...seen]), last_trade_check: today }, trades };
}

function fmtDay(dateStr) {
	const d = new Date(`${dateStr}T00:00:00Z`);
	return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

// Group issue rows by their own series (an annual is reported under its own
// label), in issue order.
function bySeries(rows) {
	const groups = new Map();
	for (const r of rows) {
		const key = r.series?.id ?? "?";
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key).push(r);
	}
	for (const list of groups.values()) list.sort((a, b) => compareIssueNumbers(a.number ?? "", b.number ?? ""));
	return groups;
}

function seriesTitle(rows, fallback) {
	const label = rows[0]?.label ?? "";
	const i = label.lastIndexOf(" #");
	return i > 0 ? label.slice(0, i) : fallback;
}

// Bell notices for one subscription's check. ids are stable per series + day
// (or per trade), so addNotification's dedupe keeps a re-run quiet.
export function subscriptionNotices(sub, { announced = [], out = [], trades = [] }, today, nowIso) {
	const route = `/comics/series/${sub.series_id}`;
	const base = { media_type: "comic", route, created_at: nowIso, read: false, item_id: `series:${sub.series_id}` };
	const notices = [];
	const notify = sub.notify ?? { announced: true, out: true };

	if (notify.announced !== false) {
		for (const [sid, rows] of bySeries(announced)) {
			const title = seriesTitle(rows, sub.label);
			const first = rows[0];
			notices.push({
				...base,
				id: `comic-announced:${sid}:${today}`,
				type: "comic-announced",
				title,
				cover_url: first.cover_url ?? null,
				body:
					rows.length === 1
						? `#${first.number} announced · on sale ${fmtDay(first.store_date)}`
						: `${rows.length} new issues announced · ${collapseIssueNumbers(rows.map((r) => r.number))}`,
			});
		}
	}
	if (notify.out !== false) {
		for (const [sid, rows] of bySeries(out)) {
			const title = seriesTitle(rows, sub.label);
			const last = rows[rows.length - 1];
			notices.push({
				...base,
				id: `comic-out:${sid}:${today}`,
				type: "comic-out",
				title,
				cover_url: last.cover_url ?? null,
				body:
					rows.length === 1
						? last.store_date === today
							? `#${last.number} is out today`
							: `#${last.number} is out (${fmtDay(last.store_date)})`
						: `${rows.length} issues out · ${collapseIssueNumbers(rows.map((r) => r.number))}`,
			});
		}
	}
	for (const b of trades) {
		const q = new URLSearchParams({ mode: "openlibrary", q: b.title ?? sub.trade_query ?? sub.label });
		notices.push({
			...base,
			id: `comic-trade:${b.ol_id}`,
			type: "comic-trade",
			title: b.title ?? "New collected edition",
			cover_url: b.cover_url ?? null,
			route: `/comics/add?${q}`,
			body: `New collected edition · ${sub.label}`,
		});
	}
	return notices;
}

// Release-schedule rows (shaped like db/media.js normalized items) for the
// upcoming issues of every subscription.
export function scheduleRows(subs) {
	const out = [];
	for (const sub of subs ?? []) {
		for (const u of sub.upcoming ?? []) {
			out.push({
				id: `comic-upcoming:${u.issue_id}`,
				media_type: "comic",
				mediaLabel: "Comic",
				title: u.label,
				cover_url: u.cover_url ?? null,
				release_date: u.store_date,
				release_year: Number(u.store_date.slice(0, 4)),
				reason: "subscription",
				secondary: `Subscribed · ${sub.label}`,
				route: `/comics/series/${u.series_id ?? sub.series_id}`,
			});
		}
	}
	return out;
}

// The trade-title query a new subscription starts with: the series name
// without its year ("X-Men (2024)" → "X-Men"). Usually worth editing to the
// collection's naming ("X-Men by Jed MacKay").
export function defaultTradeQuery(label) {
	return String(label ?? "").replace(/\s*\(\d{4}\)\s*$/, "").trim() || null;
}

// ---------- the pull list (roadmap #50) ----------

// The comics week runs Wednesday (new-comic day) to Tuesday.
export function pullWeek(today) {
	const d = new Date(`${today}T12:00:00Z`);
	const back = (d.getUTCDay() - 3 + 7) % 7;
	const start = addDays(today, -back);
	return { start, end: addDays(start, 6) };
}

// This week's and next week's issues across every subscription, by date.
export function pullList(subs, today) {
	const { start, end } = pullWeek(today);
	const nextEnd = addDays(end, 7);
	const rows = new Map();
	for (const sub of subs ?? []) {
		for (const u of [...(sub.recent_out ?? []), ...(sub.upcoming ?? [])]) {
			if (!u.store_date || rows.has(u.issue_id)) continue;
			rows.set(u.issue_id, { ...u, series_label: sub.label, route: `/comics/series/${u.series_id ?? sub.series_id}`, out: u.store_date <= today });
		}
	}
	const all = [...rows.values()].sort((a, b) => a.store_date.localeCompare(b.store_date) || (a.label ?? "").localeCompare(b.label ?? ""));
	return {
		start,
		end,
		thisWeek: all.filter((r) => r.store_date >= start && r.store_date <= end),
		nextWeek: all.filter((r) => r.store_date > end && r.store_date <= nextEnd),
	};
}
